/**
 * Bramble mound (`hush.bramble.v1`) — a low tangle of bush for the hideout's ground line.
 *
 * Frame 120×60, support at the bottom-center (60, 60). Overlapping lobed leaf clumps (dark at the back and base,
 * lighter on top) build a mound; a few dark arching stems with thorn ticks poke out of it, and a handful of tiny
 * pale four-petal flowers sit singly along the upper rim (never in dot pairs: no face-like marks).
 * `density` (0.4..1) sets clump count and mound height; `spread` (0.6..1) sets the mound width.
 * No definitions, no collision or gameplay geometry. Deterministic for the recipe; the seed varies the tangle.
 */
import {
  checkEnvelope, checkScope, el, mix, n, partGroup, rngFor,
  type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette,
} from '../pieces/svg';

export interface BrambleParams {
  readonly density: number; // 0.4..1
  readonly spread: number;  // 0.6..1
}
export type BrambleRecipe = PieceRecipe<'hush.bramble.v1', BrambleParams>;

export const BRAMBLE_FRAME = { w: 120, h: 60 } as const;

export const HUSH_BRAMBLE_MOUND: BrambleRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'hush.bramble.mound.v1', generatorId: 'hush.bramble.v1',
  seed: 20067018, paletteId: 'coastal_salt', params: { density: 0.8, spread: 0.9 },
};

/** rngFor's first draw is correlated across similar channel names; a murmur finalizer decorrelates it. */
function scrambled(base: (ch: string) => number): (ch: string) => number {
  return ch => {
    let h = Math.floor(base(ch) * 4294967296) >>> 0;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

function num(p: unknown, k: string, lo: number, hi: number): number {
  const v = (p as Record<string, unknown>)[k];
  if (typeof v !== 'number' || !(v >= lo && v <= hi)) throw new Error(`hush.bramble.v1: ${k} must be ${lo}..${hi}`);
  return v;
}

const W = BRAMBLE_FRAME.w, H = BRAMBLE_FRAME.h;
const clampY = (y: number) => Math.min(H - 0.9, Math.max(1.2, y));
const clampX = (x: number) => Math.min(W - 1.2, Math.max(1.2, x));

/** Lobed clump outline: `lobes` rounded bumps around an ellipse; the bottom is flattened at the ground. */
function clump(cx: number, cy: number, r: number, lobes: number, wobble: (i: number) => number): string {
  const pts: Point[] = [];
  for (let i = 0; i < lobes; i++) {
    const a = (i / lobes) * Math.PI * 2;
    const rr = r * (0.9 + 0.12 * wobble(i));
    pts.push({ x: clampX(cx + Math.cos(a) * rr), y: clampY(cy + Math.sin(a) * rr * 0.85) });
  }
  let d = '';
  for (let i = 0; i < lobes; i++) {
    const p = pts[i], q = pts[(i + 1) % lobes];
    const a = ((i + 0.5) / lobes) * Math.PI * 2;
    const c = { x: clampX(cx + Math.cos(a) * r * 1.22), y: clampY(cy + Math.sin(a) * r * 1.05) };
    d += (i === 0 ? `M${n(p.x)} ${n(p.y)}` : '') + `Q${n(c.x)} ${n(c.y)} ${n(q.x)} ${n(q.y)}`;
  }
  return d + 'Z';
}

export function buildBramble(recipe: BrambleRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  checkEnvelope(recipe, 'hush.bramble.v1');
  checkScope(scope);
  const density = num(recipe.params, 'density', 0.4, 1);
  const spread = num(recipe.params, 'spread', 0.6, 1);
  const rnd = scrambled(rngFor(recipe));
  const parts: string[] = [];
  const outline = roles.outline;

  const half = (W - 8) * spread / 2;
  const mound = Math.min(H - 6, 30 + 22 * density);
  const dome = (x: number) => H - mound * Math.sqrt(Math.max(0, 1 - ((x - W / 2) / half) ** 2));

  // Clumps: a guaranteed base row along the ground, then a scatter filling the dome.
  type Clump = { x: number; y: number; r: number; key: string };
  const clumps: Clump[] = [];
  const baseCount = Math.max(4, Math.round(half / 8.5));
  for (let i = 0; i < baseCount; i++) {
    const x = W / 2 - half + 8 + (i / (baseCount - 1)) * (2 * half - 16);
    clumps.push({ x, y: H - 8, r: 9.5 + rnd(`base.${i}.r`) * 3, key: `base.${i}` });
  }
  const scatter = Math.round(6 + 14 * density);
  for (let i = 0; i < scatter; i++) {
    const x = W / 2 + (rnd(`clump.${i}.x`) * 2 - 1) * (half - 9);
    const r = 7 + rnd(`clump.${i}.r`) * 5 * (0.7 + 0.3 * density);
    const top = dome(x) + r * 0.85;
    const y = top + rnd(`clump.${i}.y`) * Math.max(0, H - 8 - top);
    clumps.push({ x, y: Math.min(y, H - r * 0.6), r, key: `clump.${i}` });
  }
  // Back (higher) clumps first; darker low in the mound, lighter where the top catches light.
  clumps.sort((a, b) => a.y - b.y);
  const greens = [roles.reedShadow, roles.moss, roles.reedBase];
  const leafMark = (c: Clump) => {
    let s = '';
    // One or two single curved leaf-edge strokes at loose angles (paired chevrons read as closed eyes).
    for (let j = 0; j < 2; j++) {
      if (j && rnd(`${c.key}.mark.skip`) < 0.5) break;
      const a = rnd(`${c.key}.mark.${j}.a`) * Math.PI * 2, d = c.r * 0.55 * rnd(`${c.key}.mark.${j}.d`);
      const x = c.x + Math.cos(a) * d, y = c.y + Math.sin(a) * d * 0.8 + 1.5;
      const tilt = (rnd(`${c.key}.mark.${j}.t`) - 0.5) * 3;
      s += `M${n(x - 2.4)} ${n(y + tilt)}q2.4 2 ${n(4.8)} ${n(-2 * tilt)}`;
    }
    return el('path', { d: s, fill: 'none', stroke: roles.reedShadow, 'stroke-width': 0.6, opacity: 0.55 });
  };
  const drawClump = (c: Clump) => {
    const lift = 1 - (c.y - (H - mound)) / mound; // 1 at the top of the mound, 0 at the ground
    const idx = Math.min(2, Math.max(0, Math.floor(lift * 2.2 + rnd(`${c.key}.c`) * 0.9)));
    const d = clump(c.x, c.y, c.r, 7, i => rnd(`${c.key}.w${i}`) * 2 - 1);
    const hi = clump(c.x - c.r * 0.22, c.y - c.r * 0.3, c.r * 0.5, 5, i => rnd(`${c.key}.h${i}`) * 2 - 1);
    return el('path', { d, fill: greens[idx], stroke: outline, 'stroke-width': 1.1, 'stroke-linejoin': 'round' })
      + el('path', { d: hi, fill: mix(roles.reedLight, greens[idx], 0.35), opacity: 0.75 })
      + leafMark(c);
  };
  // Dark backing mass under the clumps so the mound reads as one tangle, not separate balls.
  let mass = '';
  {
    const pts: Point[] = [];
    const steps = 12;
    for (let i = 0; i <= steps; i++) {
      const x = W / 2 - half + 3 + (i / steps) * (2 * half - 6);
      const y = H - (H - dome(x)) * (0.82 + rnd(`mass.${i}`) * 0.12);
      pts.push({ x, y: clampY(y) });
    }
    let d = `M${n(pts[0].x)} ${n(H - 0.9)}L${n(pts[0].x)} ${n(pts[0].y)}`;
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1], b = pts[i];
      d += `Q${n((a.x + b.x) / 2)} ${n(clampY(Math.min(a.y, b.y) - 5))} ${n(b.x)} ${n(b.y)}`;
    }
    d += `L${n(pts[pts.length - 1].x)} ${n(H - 0.9)}Z`;
    mass = el('path', { d, fill: mix(roles.reedShadow, roles.outline, 0.25), stroke: outline, 'stroke-width': 1.2, 'stroke-linejoin': 'round' });
  }
  const split = Math.floor(clumps.length * 0.55);
  const backClumps = clumps.slice(0, split).map(drawClump).join('');
  const frontClumps = clumps.slice(split).map(drawClump).join('');

  // Thorny stems: arcs out of the mound past its silhouette, with small thorn ticks along them.
  let stems = '';
  const nStems = Math.round(3 + 2 * density);
  for (let i = 0; i < nStems; i++) {
    const side = i % 2 ? 1 : -1;
    const x0 = W / 2 + side * (4 + rnd(`stem.${i}.x`) * half * 0.5);
    const y0 = H - 10 - rnd(`stem.${i}.y`) * mound * 0.4;
    const x1 = clampX(x0 + side * (14 + rnd(`stem.${i}.reach`) * 16));
    const y1 = Math.max(3, dome(x1) - 4 - rnd(`stem.${i}.up`) * 6);
    const cx = (x0 + x1) / 2 - side * 2, cy = Math.max(2, Math.min(y0, y1) - 10);
    stems += el('path', { d: `M${n(x0)} ${n(y0)}Q${n(cx)} ${n(cy)} ${n(x1)} ${n(y1)}`, fill: 'none',
      stroke: roles.woodShadow, 'stroke-width': 1.5, 'stroke-linecap': 'round' });
    let thorns = '';
    for (let t = 0.35; t < 0.96; t += 0.2) {
      const px = (1 - t) ** 2 * x0 + 2 * (1 - t) * t * cx + t * t * x1;
      const py = (1 - t) ** 2 * y0 + 2 * (1 - t) * t * cy + t * t * y1;
      const up = Math.round(t * 5) % 2 ? -1 : 1;
      thorns += `M${n(px - 0.7)} ${n(py)}L${n(px + side * 1.2)} ${n(py + up * 2.2)}L${n(px + 0.7)} ${n(py)}Z`;
    }
    stems += el('path', { d: thorns, fill: roles.outline });
  }

  // Flowers: single pale four-petal stars on the upper rim, spaced well apart.
  let flowers = '';
  const nFlowers = Math.round(2 + 3 * density);
  let lastX = -99;
  for (let i = 0; i < nFlowers; i++) {
    const x = W / 2 - half * 0.8 + ((i + 0.3 + rnd(`flower.${i}.x`) * 0.4) / nFlowers) * half * 1.6;
    if (x - lastX < 14) continue;
    lastX = x;
    const y = dome(x) + 5 + rnd(`flower.${i}.y`) * 6;
    const s = 1.9;
    flowers += el('path', {
      d: `M${n(x)} ${n(y - s)}Q${n(x + 0.5)} ${n(y - 0.5)} ${n(x + s)} ${n(y)}Q${n(x + 0.5)} ${n(y + 0.5)} ${n(x)} ${n(y + s)}`
        + `Q${n(x - 0.5)} ${n(y + 0.5)} ${n(x - s)} ${n(y)}Q${n(x - 0.5)} ${n(y - 0.5)} ${n(x)} ${n(y - s)}Z`,
      fill: roles.salt, stroke: roles.outlineSoft, 'stroke-width': 0.4,
    });
  }

  // Contact shadow: a soft band just above the ground line.
  const shadow = el('ellipse', { cx: W / 2, cy: H - 2.4, rx: half, ry: 2, fill: outline, opacity: 0.22 });

  const body = partGroup(parts, 'bramble-shadow', shadow)
    + partGroup(parts, 'bramble-back', mass + backClumps)
    + partGroup(parts, 'bramble-stems', stems)
    + partGroup(parts, 'bramble-front', frontClumps)
    + partGroup(parts, 'bramble-flowers', flowers);
  return { frame: { ...BRAMBLE_FRAME }, body, anchors: { support: { x: W / 2, y: H } }, parts };
}
